import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, ApiError } from '../lib/api';
import { requireAccess } from '../lib/guards';
import { Button } from '../components/ui/Button';
import { ErrorBanner } from '../components/ui/ErrorBanner';
import { Input } from '../components/ui/Input';
import { PageHeader } from '../components/ui/PageHeader';
import { Select } from '../components/ui/Select';

type MatchStatus = 'unmatched' | 'vorgeschlagen' | 'auto_matched' | 'manuell_matched' | 'ignoriert';

interface BankKonto {
  id: string;
  iban: string;
  bezeichnung: string;
  bankName: string;
  aktiv: boolean;
}

interface MatchBreakdownEntry {
  komponente: string;
  punkte: number;
  begruendung: string;
}

interface BankBuchung {
  id: string;
  bankKontoId: string;
  eingangsDatum: string;
  betrag: string;
  ibanGegen: string | null;
  nameGegen: string | null;
  verwendungszweck: string;
  endToEndId: string;
  matchStatus: MatchStatus;
  matchScore: number | null;
  matchBreakdown: MatchBreakdownEntry[] | null;
  matchZahlungseingangId: string | null;
  matchedAt: string | null;
  notiz: string | null;
}

const STATUS_LABELS: Record<MatchStatus, string> = {
  unmatched: 'Unmatched',
  vorgeschlagen: 'Vorschlag',
  auto_matched: 'Auto-Match',
  manuell_matched: 'Manuell',
  ignoriert: 'Ignoriert',
};

const STATUS_BADGE: Record<MatchStatus, string> = {
  unmatched: 'bg-amber-100 text-amber-800 border-amber-200',
  vorgeschlagen: 'bg-blue-100 text-blue-800 border-blue-200',
  auto_matched: 'bg-green-100 text-green-800 border-green-200',
  manuell_matched: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  ignoriert: 'bg-slate-100 text-slate-500 border-slate-200',
};

export const Route = createFileRoute('/finanzen/bank')({
  beforeLoad: () => requireAccess('/api/v1/finanzen/bank/buchungen'),
  component: BankPage,
});

type StartSyncResult =
  | {
      kind: 'data';
      bankSessionId: string;
      buchungenGesamt: number;
      buchungenNeu: number;
      buchungenDoppelt: number;
    }
  | {
      kind: 'requiresTan';
      bankSessionId: string;
      tanReference: string;
      challenge: string;
      method: 'decoupled' | 'chiptan' | 'smstan' | 'phototan' | 'unknown';
    };

function BankPage() {
  const qc = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<MatchStatus | ''>('');
  const [pinModalOpen, setPinModalOpen] = useState(false);
  const [tanModal, setTanModal] = useState<null | {
    bankSessionId: string;
    tanReference: string;
    challenge: string;
    method: 'decoupled' | 'chiptan' | 'smstan' | 'phototan' | 'unknown';
  }>(null);
  const [error, setError] = useState<string | null>(null);

  const kontenQ = useQuery({
    queryKey: ['bank', 'konten'],
    queryFn: () => api<{ items: BankKonto[] }>('/api/v1/finanzen/bank/konten'),
  });
  const aktiveKonten = (kontenQ.data?.items ?? []).filter((k) => k.aktiv);
  const defaultKontoId = aktiveKonten[0]?.id ?? null;

  const buchungenQ = useQuery({
    queryKey: ['bank', 'buchungen', statusFilter || 'all'],
    queryFn: () =>
      api<{ items: BankBuchung[] }>(
        statusFilter
          ? `/api/v1/finanzen/bank/buchungen?status=${statusFilter}&limit=300`
          : '/api/v1/finanzen/bank/buchungen?limit=300',
      ),
    refetchInterval: 60_000,
  });
  const buchungen = buchungenQ.data?.items ?? [];

  const counts = buchungen.reduce<Record<string, number>>((acc, b) => {
    acc[b.matchStatus] = (acc[b.matchStatus] ?? 0) + 1;
    return acc;
  }, {});

  const matchRunMut = useMutation({
    mutationFn: () =>
      api<{ geprueft: number; autoMatched: number; vorgeschlagen: number; unmatched: number }>(
        '/api/v1/finanzen/bank/match-run',
        { method: 'POST', body: {} },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['bank', 'buchungen'] });
    },
  });

  function onSyncStarted(result: StartSyncResult): void {
    if (result.kind === 'requiresTan') {
      setTanModal({
        bankSessionId: result.bankSessionId,
        tanReference: result.tanReference,
        challenge: result.challenge,
        method: result.method,
      });
    } else {
      setError(null);
      void qc.invalidateQueries({ queryKey: ['bank', 'buchungen'] });
    }
    setPinModalOpen(false);
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Bank"
        description="DKB-FinTS-Sync und Match-Vorschläge gegen offene Quartals-RE-Raten."
        actions={
          <div className="flex gap-2">
            <Button
              onClick={() => matchRunMut.mutate()}
              disabled={matchRunMut.isPending}
              variant="ghost"
            >
              Match neu rechnen
            </Button>
            <Button
              onClick={() => {
                setError(null);
                setPinModalOpen(true);
              }}
              disabled={!defaultKontoId}
            >
              Sync starten
            </Button>
          </div>
        }
      />

      {error ? <ErrorBanner error={error} /> : null}

      {matchRunMut.data ? (
        <div className="rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800">
          Match-Lauf: {matchRunMut.data.geprueft} geprüft, {matchRunMut.data.autoMatched} auto,{' '}
          {matchRunMut.data.vorgeschlagen} Vorschläge, {matchRunMut.data.unmatched} unmatched.
        </div>
      ) : null}

      <div className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm">
        <div className="flex flex-wrap items-center gap-3 text-sm">
          <span className="font-medium text-slate-700">Filter:</span>
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as MatchStatus | '')}
            className="w-44"
          >
            <option value="">Alle ({buchungen.length})</option>
            {(Object.keys(STATUS_LABELS) as MatchStatus[]).map((s) => (
              <option key={s} value={s}>
                {STATUS_LABELS[s]} ({counts[s] ?? 0})
              </option>
            ))}
          </Select>
          {aktiveKonten.length > 0 ? (
            <span className="text-xs text-slate-500">
              Konto: {aktiveKonten[0]!.bezeichnung} ({aktiveKonten[0]!.iban})
            </span>
          ) : (
            <span className="text-xs text-amber-700">
              Kein aktives Bank-Konto. Anlegen via API (Admin).
            </span>
          )}
        </div>
      </div>

      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
        <table className="min-w-full divide-y divide-slate-200">
          <thead className="bg-slate-50">
            <tr className="text-left text-xs font-medium uppercase tracking-wide text-slate-500">
              <th className="px-3 py-2">Datum</th>
              <th className="px-3 py-2">Betrag</th>
              <th className="px-3 py-2">Gegenseite</th>
              <th className="px-3 py-2">Verwendungszweck</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">Score</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200 bg-white text-sm">
            {buchungen.map((b) => (
              <BuchungRow key={b.id} buchung={b} />
            ))}
            {buchungen.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-3 py-8 text-center text-sm text-slate-500">
                  Keine Buchungen — Sync starten oder Filter ändern.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      {pinModalOpen && defaultKontoId ? (
        <PinModal
          kontoId={defaultKontoId}
          onClose={() => setPinModalOpen(false)}
          onResult={onSyncStarted}
          onError={(msg) => {
            setError(msg);
            setPinModalOpen(false);
          }}
        />
      ) : null}

      {tanModal ? (
        <TanModal
          tanModal={tanModal}
          onClose={() => setTanModal(null)}
          onConfirmed={() => {
            setTanModal(null);
            void qc.invalidateQueries({ queryKey: ['bank', 'buchungen'] });
          }}
          onError={(msg) => {
            setError(msg);
            setTanModal(null);
          }}
        />
      ) : null}
    </div>
  );
}

function BuchungRow({ buchung: b }: { buchung: BankBuchung }) {
  const qc = useQueryClient();
  const ignoreMut = useMutation({
    mutationFn: () =>
      api(`/api/v1/finanzen/bank/buchungen/${b.id}/ignorieren`, {
        method: 'POST',
        body: { grund: 'manuell ignoriert' },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['bank', 'buchungen'] }),
  });

  const reNr = extractReNr(b.matchBreakdown);

  return (
    <tr>
      <td className="whitespace-nowrap px-3 py-2 text-slate-700">{b.eingangsDatum}</td>
      <td className="whitespace-nowrap px-3 py-2 font-mono text-slate-900">{b.betrag}</td>
      <td className="px-3 py-2">
        <div className="text-slate-700">{b.nameGegen ?? '—'}</div>
        <div className="text-xs text-slate-400">{b.ibanGegen ?? ''}</div>
      </td>
      <td className="px-3 py-2 text-slate-600">
        <div className="line-clamp-2">{b.verwendungszweck || '—'}</div>
      </td>
      <td className="whitespace-nowrap px-3 py-2">
        <span
          className={`inline-flex rounded-md border px-2 py-0.5 text-xs font-medium ${STATUS_BADGE[b.matchStatus]}`}
        >
          {STATUS_LABELS[b.matchStatus]}
        </span>
        {reNr ? <div className="mt-1 text-xs text-slate-500">{reNr}</div> : null}
      </td>
      <td className="whitespace-nowrap px-3 py-2 text-slate-600">
        {b.matchScore !== null ? `${b.matchScore}` : '—'}
      </td>
      <td className="whitespace-nowrap px-3 py-2 text-right">
        {b.matchStatus === 'vorgeschlagen' || b.matchStatus === 'unmatched' ? (
          <Button variant="ghost" onClick={() => ignoreMut.mutate()} disabled={ignoreMut.isPending}>
            Ignorieren
          </Button>
        ) : null}
      </td>
    </tr>
  );
}

function extractReNr(breakdown: MatchBreakdownEntry[] | null): string | null {
  if (!breakdown) return null;
  const m = breakdown.find((b) => b.komponente === 'rechnungsnummer_im_verwendungszweck');
  if (!m) return null;
  const match = /RE-\d{4}-\d+/.exec(m.begruendung);
  return match ? match[0] : null;
}

function PinModal({
  kontoId,
  onClose,
  onResult,
  onError,
}: {
  kontoId: string;
  onClose: () => void;
  onResult: (r: StartSyncResult) => void;
  onError: (msg: string) => void;
}) {
  const [pin, setPin] = useState('');
  const today = new Date().toISOString().slice(0, 10);
  const past = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
  const [fromIso, setFromIso] = useState(past);
  const [toIso, setToIso] = useState(today);

  const startMut = useMutation({
    mutationFn: () =>
      api<StartSyncResult>(`/api/v1/finanzen/bank/konten/${kontoId}/sync`, {
        method: 'POST',
        body: { fromIso, toIso, pin },
      }),
    onSuccess: (r) => onResult(r),
    onError: (e) => {
      onError(e instanceof ApiError ? e.message : String(e));
    },
  });

  return (
    <Modal title="Bank-Sync starten" onClose={onClose}>
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-slate-700">Von</label>
            <Input type="date" value={fromIso} onChange={(e) => setFromIso(e.target.value)} />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-700">Bis</label>
            <Input type="date" value={toIso} onChange={(e) => setToIso(e.target.value)} />
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-700">DKB-Online-Banking-PIN</label>
          <Input
            type="password"
            value={pin}
            onChange={(e) => setPin(e.target.value)}
            autoFocus
            autoComplete="off"
          />
          <p className="mt-1 text-xs text-slate-500">
            Wird nur für diesen Abruf verwendet, niemals gespeichert.
          </p>
        </div>
      </div>
      <div className="mt-4 flex justify-end gap-2">
        <Button variant="ghost" onClick={onClose}>
          Abbrechen
        </Button>
        <Button onClick={() => startMut.mutate()} disabled={startMut.isPending || pin.length === 0}>
          {startMut.isPending ? 'Starte…' : 'Sync starten'}
        </Button>
      </div>
    </Modal>
  );
}

function TanModal({
  tanModal,
  onClose,
  onConfirmed,
  onError,
}: {
  tanModal: {
    bankSessionId: string;
    tanReference: string;
    challenge: string;
    method: 'decoupled' | 'chiptan' | 'smstan' | 'phototan' | 'unknown';
  };
  onClose: () => void;
  onConfirmed: () => void;
  onError: (msg: string) => void;
}) {
  const [tan, setTan] = useState('');
  const isDecoupled = tanModal.method === 'decoupled';

  const confirmMut = useMutation({
    mutationFn: () =>
      api(`/api/v1/finanzen/bank/sync/confirm`, {
        method: 'POST',
        body: isDecoupled
          ? { bankSessionId: tanModal.bankSessionId, tanReference: tanModal.tanReference }
          : {
              bankSessionId: tanModal.bankSessionId,
              tanReference: tanModal.tanReference,
              tan,
            },
      }),
    onSuccess: () => onConfirmed(),
    onError: (e) => {
      onError(e instanceof ApiError ? e.message : String(e));
    },
  });

  return (
    <Modal title="TAN-Bestätigung" onClose={onClose}>
      <div className="space-y-3 text-sm">
        <div className="rounded-md border border-blue-200 bg-blue-50 p-3 text-blue-800">
          {tanModal.challenge}
        </div>
        {!isDecoupled ? (
          <div>
            <label className="block text-xs font-medium text-slate-700">TAN</label>
            <Input
              type="text"
              value={tan}
              onChange={(e) => setTan(e.target.value)}
              autoFocus
              inputMode="numeric"
              autoComplete="off"
            />
          </div>
        ) : (
          <p className="text-slate-600">
            Bitte in der DKB-App bestätigen, dann auf <em>„Bestätigt“</em> klicken.
          </p>
        )}
      </div>
      <div className="mt-4 flex justify-end gap-2">
        <Button variant="ghost" onClick={onClose}>
          Abbrechen
        </Button>
        <Button
          onClick={() => confirmMut.mutate()}
          disabled={confirmMut.isPending || (!isDecoupled && tan.length === 0)}
        >
          {confirmMut.isPending ? 'Prüfe…' : isDecoupled ? 'Bestätigt' : 'TAN absenden'}
        </Button>
      </div>
    </Modal>
  );
}

function Modal({
  title,
  children,
  onClose,
}: {
  title: string;
  children: React.ReactNode;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
      <div className="w-full max-w-md rounded-lg bg-white p-5 shadow-xl">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-base font-semibold text-slate-900">{title}</h2>
          <button
            onClick={onClose}
            className="rounded text-sm text-slate-400 hover:text-slate-600"
            aria-label="Schließen"
          >
            ✕
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}
